import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import Typography from '@material-ui/core/Typography';
import TablePagination from '@material-ui/core/TablePagination';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Theme, StyleRulesCallback, withStyles, WithStyles } from '@material-ui/core/styles';
import ViewHeaderBar from '../components/ViewHeaderBar';
import { localizations } from '../localization/localization';
import { connect } from 'react-redux';
import { Doughnut } from 'react-chartjs-2';
import * as StatisticsStore from './store/statisticsStore';
import { ApplicationState } from "../store";
import './Statistics.css';
import StatisticsContainer from './components/StatisticsContainer';
import StatisticsBlock from './components/StatisticsBlock';
import StatisticsBlockSmall from './components/StatisticsBlockSmall';
import { DeletedStatisticsModel, CompanyContractModel, StatisticsFilter } from './StatisticsModel';
import * as moment from 'moment';
import { renderDateEdit } from '../common/forms/FieldCreators';

const styles: StyleRulesCallback<Theme, object> = (theme: Theme) => ({
    card: {
        maxWidth: 400,
    }
});

const statisticColor1 = '#013759';
const statisticColor2 = '#40A6AE';
const statisticColor3 = '#ED1C29';
const statisticColor4 = '#C7CBD5';

const colors = [
    statisticColor1,
    statisticColor2,
    statisticColor3,
    statisticColor4
]

const getDataSet = (data: DeletedStatisticsModel) => {
    return {
        labels: data.deletedReasonStatistics.map(item => localizations.trucks.deleteReasons[item.deletedReasonName]),
        datasets: [{
            data: data.deletedReasonStatistics.map(item => item.removedCount),
            backgroundColor: colors,
            hoverBorderWidth: [
                10,
                10,
                10,
                10,
            ],
            hoverBorderColor: colors
        }]
    }
}

const getUnitDataSet = (data: DeletedStatisticsModel) => {
    return {
        labels: data.deletedReasonStatistics.map(item => localizations.units.deleteReasons[item.deletedReasonName]),
        datasets: [{
            data: data.deletedReasonStatistics.map(item => item.removedCount),
            backgroundColor: colors,
            hoverBorderWidth: [
                10,
                10,
                10,
                10,
            ],
            hoverBorderColor: colors
        }]
    }
}

enum ViewType {
    AllReports,
    PersonalReports
}
interface StatisticsViewState {
    viewType: ViewType;
    unitPage: number;
    truckPage: number;
    unitRowsPerPage: number;
    truckRowsPerPage: number;
    filters: StatisticsFilter;
}

type WithStylesProps = "card" | "";
type StatisticsViewProps = WithStyles<WithStylesProps> & typeof StatisticsStore.actionCreators & StatisticsStore.StatisticsState
class StatisticsView extends React.Component<StatisticsViewProps, StatisticsViewState> {

    constructor(props: StatisticsViewProps) {
        super(props);

        const startdate = moment().startOf("day");

        this.state = {
            viewType: ViewType.AllReports,
            unitPage: 0,
            truckPage: 0,
            unitRowsPerPage: 10,
            truckRowsPerPage: 10,
            filters: {
                startTime: startdate,
                endTime: startdate.clone().add(7, "days"),
                showOnlyOwnCompany: false
            }
        }

        this.handleViewTypeChange = this.handleViewTypeChange.bind(this);
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
        this.handleChangePage = this.handleChangePage.bind(this);
        this.handleCompanyCheck = this.handleCompanyCheck.bind(this);
        this.handleStartTimeChange = this.handleStartTimeChange.bind(this);
        this.handleEndTimeChange = this.handleEndTimeChange.bind(this);
    }

    public componentDidMount() {
        this.props.requestStatisticsData(this.state.filters);
    }

    public render() {
        const { viewType } = this.state;
        const { classes, isLoading } = this.props;

        return <div>
            <ViewHeaderBar title={localizations.statistics.statistics} showHelp />
            <Grid container direction="row" spacing={2} alignItems="center" >
                <Grid item style={{ paddingTop: 24 }}>
                    <Grid container direction="column" alignItems="flex-end" justify="flex-end">
                        <Grid item>
                            <FormControlLabel control={<Checkbox onChange={this.handleCompanyCheck} />} label={localizations.statistics.showOnlyPersonal}/>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    {renderDateEdit({ label: localizations.common.startTime, onChange: this.handleStartTimeChange, value: this.state.filters.startTime, minDate: moment("2018-01-01", "YYYY-MM-DD") })}
                </Grid>
                <Grid item>
                    {renderDateEdit({ label: localizations.common.endTime, onChange: this.handleEndTimeChange, value: this.state.filters.endTime })}
                </Grid>
            </Grid>
            {this.getContentByViewType(viewType, { isLoading })}
        </div>;
    }

    private handleStartTimeChange(date: moment.Moment) {

        let endDate = this.state.filters.endTime;
        if (endDate.isBefore(date)) {
            endDate = date;
        }

        const filters = {
            ...this.state.filters,
            startTime: date,
            endTime: endDate
        }

        this.setState({
            filters
        })

        this.props.requestStatisticsData(filters);
    }

    private handleEndTimeChange(date: moment.Moment) {

        let startDate = this.state.filters.startTime;
        if (startDate.isAfter(date)) {
            startDate = date;
        }

        const filters = {
            ...this.state.filters,
            endTime: date,
            startTime: startDate
        }

        this.setState({
            filters
        });

        this.props.requestStatisticsData(filters);
    }

    private handleCompanyCheck(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {

        const filters = {
            ...this.state.filters,
            showOnlyOwnCompany: checked
        }

        this.setState({
            filters
        })

        this.props.requestStatisticsData(filters);
    }

    private getContentByViewType(viewType: ViewType, { isLoading }: any) {
        switch (viewType) {
            case ViewType.AllReports:
                return <Grid container direction="column">
                    <StatisticsContainer title={localizations.statistics.reports}>
                        <StatisticsBlockSmall title={localizations.menus.freeUnits}>
                            {this.getDepartingUnits()}
                        </StatisticsBlockSmall>
                        <StatisticsBlockSmall title={localizations.menus.freeTrucks}>
                            {this.getDepartingTrucks()}
                        </StatisticsBlockSmall>
                    </StatisticsContainer>
                    <StatisticsContainer title={localizations.statistics.deletedTrucksAndUnits}>
                        <StatisticsBlock title={localizations.menus.freeUnits}>
                            {this.getUnitStatistics()}
                        </StatisticsBlock>
                        <StatisticsBlock title={localizations.menus.freeTrucks}>
                            {this.getTrucksStatistics()}
                        </StatisticsBlock>
                    </StatisticsContainer>
                    <StatisticsContainer title={localizations.statistics.reportsFromSystem}>
                        <StatisticsBlock title={localizations.menus.freeUnits}>
                            {this.getCompanyDataUnits()}
                        </StatisticsBlock>
                        <StatisticsBlock title={localizations.menus.freeTrucks}>
                            {this.getCompanyDataTrucks()}
                        </StatisticsBlock>
                    </StatisticsContainer>
                </Grid>;
            case ViewType.PersonalReports:
                return <div>
                    Personal reports
            </div>;
        }

        return null;
    }

    private getTrucksStatistics() {
        let data = {};
        if (this.props.data) {
            data = getDataSet(this.props.data.deletedTrucks);
        }

        return (
            <Doughnut options={{ responsive: true, maintainAspectRatio: true }} data={data} legend={{ position: "bottom" }} />
        );
    }
    private getUnitStatistics() {

        let data = {};
        if (this.props.data) {
            data = getUnitDataSet(this.props.data.deletedUnits);
        }

        return (
            <Doughnut options={{ responsive: true, maintainAspectRatio: true }} data={data} legend={{ position: "bottom" }} />
        );
    }

    private getDepartingTrucks() {
        const countTrucks = this.props.data ? this.props.data.truckCount.totalCount : 0;
        return (
            <Typography gutterBottom color="primary" align="center" variant="h2" >
                {countTrucks}
            </Typography>
        );
    }
    private getDepartingUnits() {
        const countUnits = this.props.data ? this.props.data.unitCount.totalCount : 0;
        return (
            <Grid container justify="center">
                <Grid item xs={12}>
                    <Typography color="primary" gutterBottom align="center" variant="h2" >
                        {countUnits}
                    </Typography>
                </Grid>
            </Grid>
        );
    }
    private getCompanyDataUnits() {

        let data: CompanyContractModel[] = [];

        if (this.props.data && this.props.data.unitContracts) {
            data = this.props.data.unitContracts;
        }

        return (
            <Grid item>
                <Table>
                    <TableBody>
                        {data.slice(this.state.unitPage * this.state.unitRowsPerPage, this.state.unitPage * this.state.unitRowsPerPage + this.state.unitRowsPerPage).map((n, i) => {
                            return (
                                <TableRow key={i}>
                                    <TableCell component="th" scope="row">
                                        {n.companyName}
                                    </TableCell>
                                    <TableCell align="right">{n.contractCount}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={data.length}
                    rowsPerPage={this.state.unitRowsPerPage}
                    labelRowsPerPage={localizations.paging.rowsPerPage}
                    page={this.state.unitPage}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={this.handleChangePage("unitPage")}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage("unitRowsPerPage")}
                />
            </Grid>
        );
    }
    private getCompanyDataTrucks() {

        let data: CompanyContractModel[] = [];

        if (this.props.data && this.props.data.truckContracts) {
            data = this.props.data.truckContracts;
        }

        return (
            <Grid item>
                <Table>
                    <TableBody>
                        {data.slice(this.state.truckPage * this.state.truckRowsPerPage, this.state.truckPage * this.state.truckRowsPerPage + this.state.truckRowsPerPage).map((n, i) => {
                            return (
                                <TableRow key={i}>
                                    <TableCell component="th" scope="row">
                                        {n.companyName}
                                    </TableCell>
                                    <TableCell align="right">{n.contractCount}</TableCell>

                                </TableRow>
                            );
                        })}
                    </TableBody>

                    <TablePagination
                        component="div"
                        count={data.length}
                        rowsPerPage={this.state.truckRowsPerPage}
                        labelRowsPerPage={localizations.paging.rowsPerPage}
                        page={this.state.truckPage}
                        backIconButtonProps={{
                            'aria-label': 'Previous Page',
                        }}
                        nextIconButtonProps={{
                            'aria-label': 'Next Page',
                        }}
                        onChangePage={this.handleChangePage("truckPage")}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage("truckRowsPerPage")}
                    />
                </Table>
            </Grid>
        );
    }

    private handleChangePage = (key: "unitPage" | "truckPage") => (event: any, page: number) => {
        this.setState({
            ...this.state,
            [key]: page
        });
    };

    private handleChangeRowsPerPage = (key: "unitRowsPerPage" | "truckRowsPerPage") => (event: any) => {
        this.setState({
            ...this.state,
            [key]: event.target.value
        });
    };

    private handleViewTypeChange(event: any, value: ViewType) {
        this.setState({
            viewType: value
        });
    }
}
const mapStateToProps = (state: ApplicationState) => {
    return state.statistics;
}

export default withStyles(styles)(connect<any, any, any>(mapStateToProps, StatisticsStore.actionCreators)(StatisticsView));



// WEBPACK FOOTER //
// ./src/statistics/StatisticsView.tsx